import React, { createContext, useState, useCallback, useRef } from "react";
import { makeAxiosCall } from "utils";
import { CartContext } from "./Cart";
import { UserContext } from "./User";

export interface VolumeTier {
  qty: number;
  price: number;
}

export interface PriceData {
  basePrice: number;
  resolvedPrice: number;
  discount: number;
  discountPercent: number;
  appliedRule: { id: number; name: string; rule_type: number; rule_value: number } | null;
  appliedPortalRule?: {
    id: number;
    name: string;
    discount_type: string;
    discount_value: number;
    stack_on_resolved_price: boolean;
  } | null;
  volumeTiers?: VolumeTier[];
}

interface PricingContextInterface {
  prices: Record<string, PriceData>;
  loading: boolean;
  resolveForProducts: (productIds: number[], companyId: number, quantities?: Record<number, number>) => Promise<void>;
  getPrice: (productId: number, qty?: number) => PriceData | null;
}

export const PricingContext = createContext<PricingContextInterface>({
  prices: {},
  loading: false,
  resolveForProducts: async () => {},
  getPrice: () => null,
});

// Prices are cached per (product, quantity) so volume pricing resolves
// correctly when the same product is requested at different quantities.
const cacheKey = (productId: number | string, qty: number) => `${productId}:${qty}`;

export const PricingProvider = ({ children }: { children: React.ReactNode }) => {
  const [prices, setPrices] = useState<Record<string, PriceData>>({});
  const [loading, setLoading] = useState(false);
  const lastCacheKeyRef = useRef<string | null>(null);
  const { orderingOnBehalfOf, currentOrder } = React.useContext(CartContext);
  const { currentUser } = React.useContext(UserContext);

  const companyType = currentUser?.company?.companyType;
  const isInternal = companyType === "Internal";
  const isRep = companyType === "Rep";
  const isDemoOrder = !!currentOrder?.demoOrder;

  // Ordering on behalf of a *different* company resolves that company's full
  // (company-specific) pricing. Otherwise — plain browsing, or on behalf of
  // yourself — internal users/reps get only base + universal/qty rules, so their
  // own internal or demo pricing never shows in the catalog.
  const isOnBehalfOfOther =
    !!orderingOnBehalfOf && orderingOnBehalfOf.id != currentUser?.company?.id;
  const qtyRulesOnly = (isInternal || (isRep && !isDemoOrder)) && !isOnBehalfOfOther;

  // Clear cache when company or rule mode changes
  React.useEffect(() => {
    const companyId = orderingOnBehalfOf?.id || currentUser?.company?.id;
    const cacheKey = `${companyId}-${qtyRulesOnly}`;
    if (lastCacheKeyRef.current && cacheKey !== lastCacheKeyRef.current) {
      setPrices({});
    }
    lastCacheKeyRef.current = cacheKey;
  }, [orderingOnBehalfOf?.id, currentUser?.company?.id, qtyRulesOnly]);

  const resolveForProducts = useCallback(async (
    productIds: number[],
    companyId: number,
    quantities?: Record<number, number>
  ) => {
    const qtyFor = (id: number) => quantities?.[id] || 1;

    // Filter to only (product, qty) pairs not yet cached
    const uncachedIds = productIds.filter((id) => !prices[cacheKey(id, qtyFor(id))]);
    if (uncachedIds.length === 0) return;

    setLoading(true);
    try {
      const response = await makeAxiosCall("post", "portal/prices/resolve", {
        product_ids: uncachedIds,
        company_id: companyId,
        quantities: quantities || {},
        qty_rules_only: qtyRulesOnly,
      });

      if (response?.data?.prices) {
        setPrices((prev) => {
          const next = { ...prev };
          for (const [id, data] of Object.entries(response.data.prices)) {
            next[cacheKey(id, qtyFor(Number(id)))] = data as PriceData;
          }
          return next;
        });
      }
    } catch (err) {
      console.error("Failed to resolve prices:", err);
    } finally {
      setLoading(false);
    }
  }, [prices, qtyRulesOnly]);

  const getPrice = useCallback((productId: number, qty: number = 1): PriceData | null => {
    return prices[cacheKey(productId, qty)] || null;
  }, [prices]);

  return (
    <PricingContext.Provider value={{ prices, loading, resolveForProducts, getPrice }}>
      {children}
    </PricingContext.Provider>
  );
};
